import { useMemo, useState, useRef, CSSProperties, useEffect } from "react";
import { TreeSelectItemDto } from "../../types/SelectItemDto";
import './AppHierarchicalMultiSelect.css'
import { useTranslation } from "react-i18next";

interface AppHierarchicalMultiSelectProps {
    options: TreeSelectItemDto[];
    selectedValues: number[];
    onChange: (ids: number[]) => void;
    placeholder?: string;
    disabled?: boolean;
    isInvalid?: boolean;
    className?: string;
    style?: CSSProperties;
}

export default function AppHierarchicalMultiSelect({
    options,
    selectedValues,
    onChange,
    placeholder,
    disabled = false,
    isInvalid = false,
    className,
    style
}: AppHierarchicalMultiSelectProps) {    
    const { t } = useTranslation(['common']);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    
    const byParent = useMemo(() => {
        const map: Record<string, TreeSelectItemDto[]> = {};
        const idsInList = new Set(options.map((o) => o.id));

        options.forEach((option) => {
            const hasParentInList = option.parentId != null && idsInList.has(option.parentId);
            const key = hasParentInList ? option.parentId! : "root";
            if (!map[key]) map[key] = [];
            map[key].push(option);
        });
        return map;
    }, [options]);

    const getAllDescendants = (id: number): number[] => {
        const children = byParent[id] ?? [];
        return children.flatMap((c) => [c.id, ...getAllDescendants(c.id)]);
    };

    const getAllAncestors = (id: number): number[] => {
        const item = options.find((i) => i.id === id);
        if (!item || item.parentId == null) return [];
        return [item.parentId, ...getAllAncestors(item.parentId)];
    };

    const selected = new Set(selectedValues);

    const isOptionDisabled = (id: number) =>
        disabled || getAllAncestors(id).some((aid) => selected.has(aid));

    const handleToggle = (id: number) => {
        if (isOptionDisabled(id)) return;
            const next = new Set(selected);
        if (next.has(id)) {
            next.delete(id);
        } else {
            getAllDescendants(id).forEach((d) => next.delete(d));
            next.add(id);
        }
        onChange(Array.from(next));
    };

    const handleRemove = (id: number) => {
        onChange(selectedValues.filter((v) => v !== id));
    };

    const displaySelected = selectedValues.filter((id) => !isOptionDisabled(id));

    const renderTree = (parentId: number | "root", level = 0): React.ReactNode => {
        const children = byParent[parentId] ?? [];
        return children.map((item) => {
            const optionDisabled = isOptionDisabled(item.id);
            const checked = selected.has(item.id);
            
            return (
                <div key={item.id}>
                    <div
                        className={`app-hierarchical-select_option ${optionDisabled ? "disabled" : ""}`}
                        style={{ paddingLeft: `${12 + level * 18}px` }}
                        onClick={() => handleToggle(item.id)}
                    >
                        <input className="app-hierarchical-select_option-checkbox" type="checkbox" checked={checked} disabled={optionDisabled} readOnly />
                        <span className="app-hierarchical-select_option-label">{item.name ?? ""}</span>
                    </div>
                    {renderTree(item.id, level + 1)}
                </div>
            );
        });
    };

    return (
        <div className={`app-hierarchical-select_wrapper ${className}`} ref={wrapperRef} style={style}>
            <div className="app-hierarchical-select_trigger" 
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className={`app-hierarchical-select_tags ${isInvalid ? "is-invalid" : ""}`}>
                    {displaySelected.length === 0 ? (
                        <span className="app-hierarchical-select_placeholder">{placeholder ?? t("common:selectDefaultText")}</span>
                    ) : (
                        displaySelected.map((id) => {
                            const item = options.find((i) => i.id === id)!;
                            return (
                                <span key={id} className="app-hierarchical-select_tag">
                                    {item?.name ?? ""}
                                    <button 
                                        className="app-hierarchical-select_tag-remove"
                                        onClick={(e) => { e.stopPropagation(); handleRemove(id); }}>x</button>
                                </span>
                            );
                        })
                    )}
                </div>
                <span className="app-hierarchical-select_chevron">{isOpen ? "▴" : "▾"}</span>
            </div>

            {isOpen && (
                <div className="app-hierarchical-select_dropdown">
                    {renderTree("root")}
                </div>
            )}
        </div>
    );
}